import { DEFAULT_HEADERS } from '../config';
import { fetchAuthSession } from 'aws-amplify/auth';

// Type definitions
export type EntityType = 'CATEGORY' | 'SKILL' | 'SUBSKILL';

export interface BaseEntity {
  id: string;
  type: EntityType;
  entityType?: string;
  name: string;
  beginner_name: string;
  description: string;
  beginner_description: string;
  level: number;
  parent_id: string | null;
  parentId?: string | null;
  display_order: number;
  weight: number;
  created_at: string;
  updated_at: string;
  examples?: {
    beginner: string[];
    intermediate: string[];
    advanced: string[];
  } | null;
}

export interface Category extends BaseEntity {
  type: 'CATEGORY';
  learning_objectives: string[];
  key_concepts: string[];
  skills?: Skill[];
}

export interface Skill extends BaseEntity {
  type: 'SKILL';
  practice_contexts: string[];
  common_challenges: string[];
  teaching_points: string[];
  related_skills: string[];
  subskills?: SubSkill[];
}

export interface SubSkill extends BaseEntity {
  type: 'SUBSKILL';
  examples: {
    beginner: string[];
    intermediate: string[];
    advanced: string[];
  };
  counter_examples: string[];
  practice_prompts: string[];
  assessment_criteria: string[];
  collocations: string[];
  usage_notes: string[];
}

export interface HierarchyNode {
  id: string;
  name: string;
  beginner_name: string;
  description: string;
  beginner_description: string;
  type: EntityType;
  parent_id?: string;
  children?: HierarchyNode[];
  examples?: {
    beginner: string[];
    intermediate: string[];
    advanced: string[];
  };
}

export interface ImportResult {
  message: string;
  stats: {
    categories: { created: number; errors: number };
    skills: { created: number; errors: number };
    subskills: { created: number; errors: number };
  };
  errors: string[] | null;
}

const getBaseUrl = () => {
  return process.env.NODE_ENV === 'development' 
    ? '/Prod' 
    : (process.env.REACT_APP_API_BASE_URL || 'https://p811zb8knk.execute-api.us-east-1.amazonaws.com/Prod');
};
const getSecureBaseUrl = () => `${getBaseUrl()}/secure`;

const getIdToken = async () => {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString();
  } catch (error) {
    console.error('Error getting ID token:', error);
    return null;
  }
};

export const skillsService = {
  /**
   * Get categories only (for lazy loading)
   */
  getCategories: async (): Promise<Category[]> => {
    const baseUrl = getSecureBaseUrl();
    const idToken = await getIdToken();
    if (!idToken) throw new Error('Missing ID token');
    const response = await fetch(`${baseUrl}/skills-hierarchy?entity_type=CATEGORY`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${idToken}` },
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch categories: ${errorText || response.statusText}`);
    }
    return response.json();
  },

  /**
   * Presign upload for images
   */
  presignUpload: async (key: string, contentType: string): Promise<{ url: string; key: string; content_type: string }> => {
    const baseUrl = getSecureBaseUrl();
    const idToken = await getIdToken();
    if (!idToken) throw new Error('Missing ID token');
    const response = await fetch(`${baseUrl}/media/presign-upload`, {
      method: 'POST',
      headers: { ...DEFAULT_HEADERS, Authorization: `Bearer ${idToken}` },
      body: JSON.stringify({ key, content_type: contentType })
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to presign upload: ${errorText || response.statusText}`);
    }
    return response.json();
  },

  /**
   * Presign view for images
   */
  presignView: async (key: string): Promise<string> => {
    const baseUrl = getSecureBaseUrl();
    const idToken = await getIdToken();
    if (!idToken) throw new Error('Missing ID token');
    const response = await fetch(`${baseUrl}/media/presign-view?key=${encodeURIComponent(key)}`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${idToken}` },
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to presign view: ${errorText || response.statusText}`);
    }
    const data = await response.json();
    return data.url as string;
  },
  /**
   * Get the complete skills hierarchy
   */
  getSkillsHierarchy: async (): Promise<Category[]> => {
    console.log('Calling getSkillsHierarchy API');
    const baseUrl = getSecureBaseUrl();
    try {
      const idToken = await getIdToken();
      if (!idToken) throw new Error('Missing ID token');
      const response = await fetch(`${baseUrl}/skills-hierarchy`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${idToken}` },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API error:', errorText);
        throw new Error(`Failed to fetch skills hierarchy: ${errorText || response.statusText}`);
      }

      const data = await response.json();
      console.log('API response:', data);
      
      // Add detailed logging of the hierarchy structure
      data.forEach((category: Category) => {
        console.log(`Category: ${category.name} (${category.id})`);
        console.log('Skills:', category.skills?.length || 0);
        category.skills?.forEach(skill => {
          console.log(`  Skill: ${skill.name} (${skill.id}) - Parent ID: ${skill.parent_id}`);
          console.log('  SubSkills:', skill.subskills?.length || 0);
        });
      });
      
      return data;
    } catch (error) {
      console.error('Error in getSkillsHierarchy:', error);
      throw error;
    }
  },

  /**
   * Get entity by ID and type
   */
  getEntityById: async (entityType: EntityType, entityId: string): Promise<BaseEntity> => {
    console.log(`Fetching ${entityType} with ID ${entityId}`);
    const baseUrl = getSecureBaseUrl();
    try {
      // Prefer path-based GET-by-ID for clarity and caching
      const idToken = await getIdToken();
      if (!idToken) throw new Error('Missing ID token');
      const response = await fetch(`${baseUrl}/skills-hierarchy/${entityType}/${entityId}`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${idToken}` },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch ${entityType.toLowerCase()} with ID ${entityId}: ${errorText}`);
      }

      return response.json();
    } catch (error) {
      console.error(`Error fetching ${entityType} with ID ${entityId}:`, error);
      throw error;
    }
  },

  /**
   * Get children of a parent entity
   */
  getChildrenByParentId: async (entityType: EntityType, parentId: string): Promise<BaseEntity[]> => {
    console.log(`Fetching ${entityType} children for parent ${parentId}`);
    const baseUrl = getSecureBaseUrl();
    try {
      const idToken = await getIdToken();
      if (!idToken) throw new Error('Missing ID token');
      const response = await fetch(
        `${baseUrl}/skills-hierarchy?entity_type=${entityType}&parent_id=${parentId}`, 
        {
          method: 'GET',
          headers: { Authorization: `Bearer ${idToken}` },
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch children for parent ID ${parentId}: ${errorText}`);
      }

      return response.json();
    } catch (error) {
      console.error(`Error fetching children for parent ${parentId}:`, error);
      throw error;
    }
  },

  /**
   * Save or update an entity
   */
  saveEntity: async (entity: BaseEntity): Promise<BaseEntity> => {
    console.log(`Saving ${entity.type || entity.entityType} entity:`, entity);
    const baseUrl = getSecureBaseUrl();
    try {
      // Log the exact JSON being sent to the backend
      const jsonBody = JSON.stringify(entity);
      console.log('Sending entity to backend:', jsonBody);
      
      const idToken = await getIdToken();
      if (!idToken) throw new Error('Missing ID token');
      const response = await fetch(`${baseUrl}/skills-hierarchy`, {
        method: 'POST',
        headers: { ...DEFAULT_HEADERS, Authorization: `Bearer ${idToken}` },
        body: jsonBody,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Save failed. Status:', response.status, 'Body:', errorText);
        throw new Error(`Failed to save entity: ${errorText}`);
      }

      const saved = await response.json();
      console.log('Save succeeded. Response:', saved);
      return saved as any;
    } catch (error) {
      console.error(`Error saving ${entity.type || entity.entityType}:`, error);
      throw error;
    }
  },

  /**
   * Delete an entity
   */
  deleteEntity: async (entityType: EntityType, entityId: string): Promise<{ message: string }> => {
    console.log(`Deleting ${entityType} with ID ${entityId}`);
    const baseUrl = getSecureBaseUrl();
    try {
      const idToken = await getIdToken();
      if (!idToken) throw new Error('Missing ID token');
      const response = await fetch(`${baseUrl}/skills-hierarchy/${entityType}/${entityId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${idToken}` },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to delete entity: ${errorText}`);
      }

      return response.json();
    } catch (error) {
      console.error(`Error deleting ${entityType} with ID ${entityId}:`, error);
      throw error;
    }
  },

  /**
   * Import skills hierarchy data in bulk
   */
  importSkillsHierarchy: async function(payload: any, clearExisting: boolean = false): Promise<ImportResult> {
    console.log('Importing skills hierarchy payload');
    const baseUrl = getSecureBaseUrl();
    try {
      // Pass-through mode:
      // If payload already has top-level keys the backend understands, send as-is;
      // otherwise wrap in { data: payload }.
      const bodyToSend = (payload && (payload.sub_skills || payload.data))
        ? { ...(payload || {}), clear_existing: clearExisting }
        : { data: payload, clear_existing: clearExisting };

      const idToken = await getIdToken();
      if (!idToken) throw new Error('Missing ID token');
      const response = await fetch(`${baseUrl}/skills-hierarchy/import`, {
        method: 'POST',
        headers: { ...DEFAULT_HEADERS, Authorization: `Bearer ${idToken}` },
        body: JSON.stringify(bodyToSend),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to import skills hierarchy: ${errorText}`);
      }

      const result = await response.json();
      console.log('Import result:', result);
      return result;
    } catch (error) {
      console.error('Error importing skills hierarchy:', error);
      throw error;
    }
  },

  generateEmbeddingsForSubskill: async function(subskillId: string): Promise<{ success: boolean; count: number; updated_subskill: any }> {
    try {
      const token = await getIdToken();
      if (!token) {
        throw new Error('Authentication token not found.');
      }
      const baseUrl = getSecureBaseUrl();
      const response = await fetch(`${baseUrl}/skills-hierarchy/generate-embedding`, {
        method: 'POST',
        headers: {
          ...DEFAULT_HEADERS,
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ subskill_id: subskillId })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate embeddings');
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error in generateEmbeddingsForSubskill:', error);
      throw error;
    }
  },

  scoreText: async function(text: string): Promise<{ success: boolean; scores: any[] }> {
    try {
      const token = await getIdToken();
      if (!token) {
        throw new Error('Authentication token not found.');
      }
      const baseUrl = getBaseUrl();
      const response = await fetch(`${baseUrl}/scoring/score-text`, {
        method: 'POST',
        headers: {
          ...DEFAULT_HEADERS,
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ text: text })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to score text');
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error in scoreText:', error);
      throw error;
    }
  },
  
  segmentConversation: async function(payload: {
    approach?: 'sliding' | 'similarity';
    params?: { include_teacher_context?: boolean };
    transcript: { speaker: string; text: string }[];
  }): Promise<{ segments: any[]; diagnostics: any }> {
    try {
      const token = await getIdToken();
      if (!token) {
        throw new Error('Authentication token not found.');
      }
      const baseUrl = getBaseUrl();
      const response = await fetch(`${baseUrl}/scoring/segment-conversation`, {
        method: 'POST',
        headers: {
          ...DEFAULT_HEADERS,
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ approach: 'sliding', ...payload })
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to segment conversation');
      }
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error in segmentConversation:', error);
      throw error;
    }
  },

  candidateSubskills: async function(payload: { segments: { id: string; text?: string; preview?: string }[]; top_k?: number, min_similarity?: number, use_hybrid?: boolean, teacher_context_chars?: number, combine_strategy?: 'max' | 'weighted', alpha_student?: number }): Promise<{ candidates: any[] }> {
    try {
      const token = await getIdToken();
      if (!token) {
        throw new Error('Authentication token not found.');
      }
      const baseUrl = getBaseUrl();
      const response = await fetch(`${baseUrl}/scoring/candidate-subskills`, {
        method: 'POST',
        headers: {
          ...DEFAULT_HEADERS,
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ top_k: 5, ...payload })
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to get candidate subskills');
      }
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error in candidateSubskills:', error);
      throw error;
    }
  },

  /**
   * Search entities by name
   */
  searchEntitiesByName: async (entityType: EntityType, searchTerm: string): Promise<BaseEntity[]> => {
    console.log(`Searching for ${entityType} with term "${searchTerm}"`);
    const baseUrl = getSecureBaseUrl();
    try {
      const token = await getIdToken();
      if (!token) throw new Error('Missing ID token');
      const response = await fetch(
        `${baseUrl}/skills-hierarchy?entity_type=${entityType}&search=${searchTerm}`, 
        {
          method: 'GET',
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to search ${entityType.toLowerCase()} entities: ${errorText}`);
      }

      return response.json();
    } catch (error) {
      console.error(`Error searching ${entityType} with term "${searchTerm}":`, error);
      throw error;
    }
  },

  /**
   * Convert hierarchy to a flat tree structure for UI components
   */
  /**
   * Get all skills (for move functionality)
   */
  getAllSkills: async (): Promise<Skill[]> => {
    const baseUrl = getSecureBaseUrl();
    const response = await fetch(`${baseUrl}/skills-hierarchy?entity_type=SKILL`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${await getIdToken()}` },
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch skills: ${errorText || response.statusText}`);
    }
    return response.json();
  },

  /**
   * Update an entity
   */
  updateEntity: async (entityType: EntityType, entity: BaseEntity): Promise<BaseEntity> => {
    const entityWithType = {
      ...entity,
      entityType: entityType
    };
    return skillsService.saveEntity(entityWithType);
  },

  convertToTreeNodes: (hierarchy: Category[]): HierarchyNode[] => {
    console.log('Converting hierarchy to tree nodes');
    return hierarchy.map(category => {
      console.log(`Processing category: ${category.name} (${category.id})`);
      const categoryNode: HierarchyNode = {
        id: category.id,
        name: category.name,
        beginner_name: category.beginner_name,
        description: category.description,
        beginner_description: category.beginner_description,
        type: 'CATEGORY' as EntityType,
        children: []
      };

      if (category.skills && category.skills.length > 0) {
        console.log(`Found ${category.skills.length} skills for category ${category.name}`);
        categoryNode.children = category.skills.map(skill => {
          console.log(`Processing skill: ${skill.name} (${skill.id}) - Parent ID: ${skill.parent_id}`);
          const skillNode: HierarchyNode = {
            id: skill.id,
            name: skill.name,
            beginner_name: skill.beginner_name,
            description: skill.description,
            beginner_description: skill.beginner_description,
            type: 'SKILL' as EntityType,
            parent_id: category.id,
            children: []
          };

          if (skill.subskills && skill.subskills.length > 0) {
            console.log(`Found ${skill.subskills.length} subskills for skill ${skill.name}`);
            skillNode.children = skill.subskills.map(subskill => {
              console.log(`Processing subskill: ${subskill.name} (${subskill.id}) - Parent ID: ${subskill.parent_id}`);
              return {
                id: subskill.id,
                name: subskill.name,
                beginner_name: subskill.beginner_name,
                description: subskill.description,
                beginner_description: subskill.beginner_description,
                type: 'SUBSKILL' as EntityType,
                parent_id: skill.id,
                examples: subskill.examples
              };
            });
          }

          return skillNode;
        });
      } else {
        console.log(`No skills found for category ${category.name}`);
      }

      return categoryNode;
    });
  }
}; 